import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Button, Typography, Spin, Empty, Card, Tag, Descriptions } from 'antd';
import { LoadingOutlined, CheckCircleOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useOrders } from '@hooks';

const { Title, Paragraph, Text } = Typography;

export const OrderPage = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const accessToken = searchParams.get('accessToken') || undefined;

  const { getOrder, createPortalSession } = useOrders();
  const { data: order, isLoading, isError } = getOrder(orderId || '', accessToken);

  const handleAccessBillingPortal = async () => {
    try {
      const response = await createPortalSession.mutateAsync();
      window.location.href = response.url;
    } catch (error) {
      // Error message handled by hook
    }
  };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      pending: 'orange',
      paid: 'green',
      shipped: 'blue',
      completed: 'purple',
      cancelled: 'red',
    };
    return statusColors[status] || 'default';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[url(/assets/images/Background.png)] bg-center bg-cover bg-no-repeat flex items-center justify-center">
        <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: '#F74902' }} spin />} />
      </div>
    );
  }

  if (isError || !order) {
    return (
      <div className="min-h-screen bg-[url(/assets/images/Background.png)] bg-center bg-cover bg-no-repeat flex items-center justify-center">
        <div className="container mx-auto px-4 py-16 text-center">
          <Empty
            description={
              <span className="font-orator text-white text-lg">
                Order not found or you don't have permission to view it.
              </span>
            }
          />
          <Button
            type="primary"
            className="btn-orangicide font-orator mt-6"
            onClick={() => navigate('/products')}
          >
            BACK TO PRODUCTS
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[url(/assets/images/Background.png)] bg-center bg-cover bg-no-repeat">
      <div className="container mx-auto px-4 py-16">
        {/* Header */}
        <div className="mb-8">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/products')}
            className="mb-4 font-orator text-lg"
          >
            Back to Products
          </Button>
          
          <div className="flex items-center gap-4 mb-4">
            <CheckCircleOutlined style={{ fontSize: 48, color: '#10b981' }} />
            <div>
              <Title level={1} className="font-displayed !font-normal text-orangicide !mb-2">
                Order Confirmed!
              </Title>
              <Paragraph className="font-orator text-white !text-xl !mb-0">
                Thank you for your purchase
              </Paragraph>
            </div>
          </div>
        </div>

        {/* Order Details Card */}
        <Card className="bg-zinc-900 border-zinc-700 mb-6">
          <div className="mb-6">
            <Text className="font-orator text-orangicide text-lg">Order Number</Text>
            <Title level={3} className="font-displayed !font-normal text-white !mt-2">
              {order.orderNumber}
            </Title>
          </div>

          <Descriptions
            column={{ xs: 1, sm: 2 }}
            className="order-descriptions"
            labelStyle={{ color: '#F74902', fontFamily: 'Orator' }}
            contentStyle={{ color: '#ffffff', fontFamily: 'Orator' }}
          >
            <Descriptions.Item label="Status">
              <Tag color={getStatusColor(order.status)} className="font-orator uppercase">
                {order.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Order Date">
              {formatDate(order.createdAt)}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {order.customerEmail}
            </Descriptions.Item>
            <Descriptions.Item label="Total Amount">
              <Text className="font-orator text-orangicide font-bold text-xl">
                ${order.totalAmount.toFixed(2)} {order.currency.toUpperCase()}
              </Text>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* Order Items */}
        <Card
          title={
            <Title level={3} className="font-displayed !font-normal text-orangicide !mb-0">
              Order Items
            </Title>
          }
          className="bg-zinc-900 border-zinc-700 mb-6"
        >
          <div className="space-y-4">
            {order.items.map((item) => (
              <div
                key={item.id}
                className="bg-zinc-800 p-4 flex justify-between items-center"
              >
                <div className="flex-1">
                  <Title level={4} className="font-displayed !font-normal text-white !mb-2">
                    {item.productName}
                  </Title>
                  <Text className="font-orator text-zinc-400">
                    Quantity: {item.quantity} x ${item.price.toFixed(2)}
                  </Text>
                </div>
                <div className="text-right">
                  <Text className="font-orator text-orangicide font-bold text-lg">
                    ${item.subtotal.toFixed(2)}
                  </Text>
                </div>
              </div>
            ))}
          </div>

          {/* Subtotal and Discount */}
          <div className="mt-6 pt-6 border-t border-zinc-700 space-y-3">
            <div className="flex justify-between items-center">
              <Text className="font-orator text-zinc-400 text-lg">Subtotal</Text>
              <Text className="font-orator text-zinc-400 text-lg">
                ${order.subtotalBeforeDiscount.toFixed(2)}
              </Text>
            </div>
            
            {order.discountAmount > 0 && (
              <div className="flex justify-between items-center bg-green-500/10 p-3 -mx-3">
                <Text className="font-orator text-green-500 text-lg">Tier Discount</Text>
                <Text className="font-orator text-green-500 font-bold text-lg">
                  -${order.discountAmount.toFixed(2)}
                </Text>
              </div>
            )}
            
            <div className="flex justify-between items-center pt-3 border-t border-zinc-600">
              <Text className="font-orator text-white text-xl">Total</Text>
              <Text className="font-orator text-orangicide font-bold text-2xl">
                ${order.totalAmount.toFixed(2)}
              </Text>
            </div>
          </div>
        </Card>

        {/* Email Confirmation Notice */}
        <Card className="bg-zinc-900 border-zinc-700 mb-6">
          <div className="flex items-start gap-4">
            <div className="text-4xl">📧</div>
            <div>
              <Title level={4} className="font-displayed !font-normal text-white !mb-2">
                Confirmation Email Sent
              </Title>
              <Paragraph className="font-orator text-zinc-300 !mb-0">
                A confirmation email with order details and tracking information has been sent to{' '}
                <Text className="text-orangicide font-bold">{order.customerEmail}</Text>
              </Paragraph>
            </div>
          </div>
        </Card>

        {/* Billing Portal Access (if authenticated) */}
        {localStorage.getItem('token') && (
          <Card className="bg-zinc-900 border-zinc-700">
            <div className="text-center">
              <Title level={4} className="font-displayed !font-normal text-white !mb-4">
                Manage Your Orders & Billing
              </Title>
              <Paragraph className="font-orator text-zinc-300 !mb-6">
                Access the billing portal to view all your orders, update payment methods, and manage your account details.
              </Paragraph>
              <Button
                type="primary"
                size="large"
                className="btn-orangicide font-orator"
                onClick={handleAccessBillingPortal}
                loading={createPortalSession.isPending}
              >
                ACCESS BILLING PORTAL
              </Button>
            </div>
          </Card>
        )}
      </div>

      <style>{`
        .order-descriptions .ant-descriptions-item-label {
          color: #F74902 !important;
          font-family: 'Orator', monospace !important;
        }
        .order-descriptions .ant-descriptions-item-content {
          color: #ffffff !important;
          font-family: 'Orator', monospace !important;
        }
        .ant-card {
          background-color: #18181b !important;
          border-color: #3f3f46 !important;
        }
        .ant-card-head {
          border-color: #3f3f46 !important;
        }
      `}</style>
    </div>
  );
};

